import { useState, useEffect, useRef } from 'react';
import { Award, MapPin, CheckCircle, TrendingUp } from 'lucide-react';

const AboutSection = () => {
  const [yearsCount, setYearsCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef(null);

  // Animated counter for years of experience
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            let count = 0;
            const target = 15; // Years of experience
            const duration = 2000; // 2 seconds
            const increment = target / (duration / 16); // 60fps

            const timer = setInterval(() => {
              count += increment;
              if (count >= target) {
                setYearsCount(target);
                clearInterval(timer);
              } else {
                setYearsCount(Math.floor(count));
              }
            }, 16);
          }
        });
      },
      {
        threshold: 0.1, // Lower threshold for better mobile detection
        rootMargin: '0px 0px -50px 0px' // Trigger slightly before element is fully visible
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [hasAnimated]);

  return (
    <section
      id="about"
      ref={sectionRef}
      className="py-20 px-6 bg-gradient-to-br from-slate-900/50 to-slate-800/50"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h3 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-amber-300 to-amber-500 bg-clip-text text-transparent">
            About SBJ Glassworks
          </h3>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Crafting excellence in interior solutions since years
          </p>
        </div>

        {/* Two Column Layout */}
        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          {/* Left Column - Company Story */}
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-8 rounded-2xl border border-slate-700">
              <h4 className="text-2xl font-bold text-white mb-4 flex items-center">
                <Award className="w-6 h-6 text-amber-400 mr-3" />
                Our Story
              </h4>
              <p className="text-slate-300 leading-relaxed mb-4">
                SBJ Glassworks has been transforming spaces across the region with
                premium window treatments, wallpaper, and glass film solutions. What started as a
                passion for interior design has grown into a trusted name in the industry.
              </p>
              <p className="text-slate-300 leading-relaxed">
                Our journey is built on a foundation of quality craftsmanship, customer satisfaction,
                and continuous innovation. Every project we undertake reflects our commitment to
                excellence and attention to detail.
              </p>
            </div>

            <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-8 rounded-2xl border border-slate-700">
              <h4 className="text-2xl font-bold text-white mb-4">
                Our Mission
              </h4>
              <p className="text-slate-300 leading-relaxed">
                To deliver exceptional interior solutions that enhance the beauty, functionality,
                and comfort of every space we touch. We believe in creating lasting value through
                superior products, expert installation, and personalized service that exceeds expectations.
              </p>
            </div>
          </div>

          {/* Right Column - Stats & Highlights */}
          <div className="space-y-6">
            {/* Years of Experience Counter */}
            <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 p-8 rounded-2xl border border-amber-500/30 text-center">
              <TrendingUp className="w-12 h-12 text-amber-400 mx-auto mb-4" />
              <div className="text-7xl font-bold bg-gradient-to-r from-amber-300 to-amber-500 bg-clip-text text-transparent mb-2">
                {yearsCount}+
              </div>
              <p className="text-xl text-slate-300 font-semibold">Years of Excellence</p>
              <p className="text-slate-400 mt-2">Serving customers with dedication and quality</p>
            </div>

            {/* Service Area */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-8 rounded-2xl border border-slate-700">
              <h4 className="text-2xl font-bold text-white mb-4 flex items-center">
                <MapPin className="w-6 h-6 text-amber-400 mr-3" />
                Service Area
              </h4>
              <p className="text-slate-300 leading-relaxed mb-4">
                We proudly serve residential and commercial clients across Delhi NCR, including
                Delhi, Gurgaon, Noida, Faridabad, and surrounding areas.
              </p>
              <p className="text-amber-400 font-semibold">
                Bringing premium interior solutions to your doorstep
              </p>
            </div>
          </div>
        </div>

        {/* Quality Commitment Section */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-8 md:p-12 rounded-2xl border border-slate-700">
          <h4 className="text-3xl font-bold text-center mb-8 bg-gradient-to-r from-amber-300 to-amber-500 bg-clip-text text-transparent">
            Our Quality Commitment
          </h4>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-slate-900" />
              </div>
              <h5 className="text-xl font-bold text-white mb-3">Premium Materials</h5>
              <p className="text-slate-300">
                We source only the finest materials from trusted suppliers, ensuring durability
                and lasting beauty in every installation.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-slate-900" />
              </div>
              <h5 className="text-xl font-bold text-white mb-3">Expert Craftsmanship</h5>
              <p className="text-slate-300">
                Our skilled team brings years of experience and meticulous attention to detail
                to every project, ensuring flawless results.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-slate-900" />
              </div>
              <h5 className="text-xl font-bold text-white mb-3">Customer Satisfaction</h5>
              <p className="text-slate-300">
                Your satisfaction is our priority. We work closely with you from consultation
                to completion, ensuring your vision becomes reality.
              </p>
            </div>
          </div>

          {/* Additional Quality Points */}
          <div className="mt-8 pt-8 border-t border-slate-700">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-amber-400 mt-1 flex-shrink-0" />
                <p className="text-slate-300">Comprehensive warranties on products and installation</p>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-amber-400 mt-1 flex-shrink-0" />
                <p className="text-slate-300">Free on-site consultation and measurements</p>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-amber-400 mt-1 flex-shrink-0" />
                <p className="text-slate-300">Timely project completion with minimal disruption</p>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-amber-400 mt-1 flex-shrink-0" />
                <p className="text-slate-300">Dedicated after-sales support and service</p>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-12">
          <p className="text-slate-300 text-lg mb-6">
            Ready to experience the SBJ Glassworks difference?
          </p>
          <a
            href="#contact"
            className="inline-block bg-gradient-to-r from-amber-500 to-amber-600 px-8 py-3 rounded-full text-lg font-semibold hover:shadow-xl hover:shadow-amber-500/50 transition-all duration-300 hover:scale-105"
          >
            Get Your Free Consultation
          </a>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
